import React, { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import {
  ArrowLeft,
  Loader2,
  TrendingUp,
  ShoppingBag,
  CheckCircle2,
  XCircle,
  DollarSign,
  Award,
  Package,
} from "lucide-react";
import { toast } from "sonner";

import { deliveryApi } from "../services/deliveryApi";
import DeliveryNav from "../components/delivery/DeliveryNav";
import CanaisStatus from "../components/delivery/CanaisStatus";

const formatBRL = (v) =>
  new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(v || 0);

export default function DeliveryDashboard() {
  const { negocioId } = useParams();
  const [loja, setLoja] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const l = await deliveryApi.getLojaByNegocio(negocioId);
      setLoja(l);
      const dash = await deliveryApi.getDashboard(l.id);
      setData(dash);
    } catch (err) {
      toast.error(
        err?.response?.status === 404
          ? "Ative o Delivery antes de ver métricas"
          : "Erro ao carregar dashboard"
      );
    } finally {
      setLoading(false);
    }
  }, [negocioId]);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[var(--primary)]" />
      </div>
    );
  }
  if (!loja || !data) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <Link to={`/empreendedor/delivery/${negocioId}`} className="text-[var(--primary)] underline">
          Voltar ao painel
        </Link>
      </div>
    );
  }

  const maxGmv = Math.max(1, ...data.serie_7d.map((d) => d.gmv));

  return (
    <div className="min-h-screen bg-[var(--bg-secondary)] pb-12">
      <DeliveryNav negocioId={negocioId} negocioNome={loja.nome_loja} />
      <div className="bg-white border-b border-[var(--border)]">
        <div className="max-w-5xl mx-auto px-4 py-3">
          <h1 className="text-lg font-bold">Dashboard de vendas</h1>
          <p className="text-xs text-[var(--text-secondary)]">
            Métricas e tendências em tempo real
          </p>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-6 space-y-6">
        {/* Status de canais de notificação */}
        <CanaisStatus negocioId={negocioId} />

        {/* Hoje */}
        <section>
          <h2 className="text-sm font-semibold mb-3 text-[var(--text-secondary)] uppercase tracking-wide">
            Hoje
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <StatCard
              icon={ShoppingBag}
              label="Pedidos"
              value={data.hoje.pedidos}
              gradient="from-blue-500 to-indigo-600"
              testid="dash-hoje-pedidos"
            />
            <StatCard
              icon={CheckCircle2}
              label="Entregues"
              value={data.hoje.entregues}
              gradient="from-emerald-500 to-teal-600"
              testid="dash-hoje-entregues"
            />
            <StatCard
              icon={DollarSign}
              label="Faturamento"
              value={formatBRL(data.hoje.gmv)}
              gradient="from-orange-500 to-red-500"
              testid="dash-hoje-gmv"
            />
            <StatCard
              icon={TrendingUp}
              label="Ticket médio"
              value={formatBRL(data.hoje.ticket_medio)}
              gradient="from-purple-500 to-pink-500"
              testid="dash-hoje-ticket"
            />
          </div>
        </section>

        {/* Série últimos 7 dias */}
        <section className="bg-white rounded-2xl border border-[var(--border)] p-4">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-sm font-semibold">Últimos 7 dias (entregues)</h2>
            <span className="text-xs text-[var(--text-secondary)]">
              Total: <strong className="text-[var(--primary)]">{formatBRL(data.semana.gmv)}</strong>
            </span>
          </div>
          <div className="flex items-end gap-2 h-32" data-testid="dash-grafico-7d">
            {data.serie_7d.map((d) => {
              const heightPct = (d.gmv / maxGmv) * 100;
              return (
                <div
                  key={d.dia}
                  className="flex-1 flex flex-col items-center justify-end"
                  title={`${d.dia}: ${formatBRL(d.gmv)} (${d.pedidos} pedidos)`}
                >
                  <div
                    className="w-full bg-gradient-to-t from-orange-500 to-red-400 rounded-t hover:opacity-80 cursor-help transition-opacity"
                    style={{ height: `${Math.max(heightPct, 2)}%` }}
                  />
                  <span className="text-[10px] text-[var(--text-secondary)] mt-1">
                    {d.dia}
                  </span>
                </div>
              );
            })}
          </div>
        </section>

        {/* Mensais e lifetime */}
        <section>
          <h2 className="text-sm font-semibold mb-3 text-[var(--text-secondary)] uppercase tracking-wide">
            Resumo geral
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <PeriodCard label="Últimos 30 dias" stats={data.mes} testid="dash-mes" />
            <PeriodCard label="Últimos 7 dias" stats={data.semana} testid="dash-semana" />
            <PeriodCard label="Total na plataforma" stats={data.lifetime} testid="dash-lifetime" />
          </div>
        </section>

        {/* Top produtos */}
        <section className="bg-white rounded-2xl border border-[var(--border)] p-4">
          <h2 className="text-sm font-semibold mb-3 flex items-center gap-2">
            <Award className="w-4 h-4 text-orange-500" /> Top produtos (30 dias)
          </h2>
          {data.top_produtos.length === 0 ? (
            <p className="text-sm text-[var(--text-secondary)]">
              Ainda sem dados suficientes — continue vendendo!
            </p>
          ) : (
            <ol className="space-y-2">
              {data.top_produtos.map((p, i) => (
                <li
                  key={p.produto_id}
                  className="flex items-center gap-3"
                  data-testid={`dash-top-${i}`}
                >
                  <span className="w-7 h-7 rounded-full bg-[var(--bg-secondary)] flex items-center justify-center text-xs font-bold">
                    {i + 1}
                  </span>
                  <Package className="w-4 h-4 text-[var(--text-secondary)]" />
                  <span className="flex-1 truncate">{p.nome}</span>
                  <span className="text-xs text-[var(--text-secondary)]">{p.quantidade}×</span>
                  <span className="font-semibold text-sm">{formatBRL(p.receita)}</span>
                </li>
              ))}
            </ol>
          )}
        </section>
      </div>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, gradient, testid }) {
  return (
    <div
      className={`bg-gradient-to-br ${gradient} text-white rounded-xl p-4 shadow-sm`}
      data-testid={testid}
    >
      <div className="flex items-center gap-2 opacity-90 text-xs uppercase tracking-wide">
        <Icon className="w-4 h-4" /> {label}
      </div>
      <div className="text-2xl font-bold mt-2">{value}</div>
    </div>
  );
}

function PeriodCard({ label, stats, testid }) {
  return (
    <div className="bg-white rounded-2xl border border-[var(--border)] p-4" data-testid={testid}>
      <h3 className="text-xs uppercase text-[var(--text-secondary)] tracking-wide font-medium">
        {label}
      </h3>
      <p className="text-2xl font-bold mt-2 text-[var(--primary)]">
        {formatBRL(stats.gmv)}
      </p>
      <div className="grid grid-cols-3 gap-1 mt-2 text-xs">
        <div>
          <p className="text-[var(--text-secondary)]">Pedidos</p>
          <p className="font-semibold">{stats.pedidos}</p>
        </div>
        <div>
          <p className="text-[var(--text-secondary)]">Entregues</p>
          <p className="font-semibold text-emerald-700">{stats.entregues}</p>
        </div>
        <div>
          <p className="text-[var(--text-secondary)]">Cancelados</p>
          <p className="font-semibold text-red-700">{stats.cancelados}</p>
        </div>
      </div>
    </div>
  );
}
